import { useMemo } from 'react';

import getEnumTranslation from '@apigrowfinance/pbolt-enum-translation';
import TextDisplayBlock from '@apigrowfinance/pbolt-text-display-block';
import { useIntl } from 'react-intl';

import { GetCardControlsResponse } from '@clients/domains/flexControls/flexControlAccountClient';
import resetPeriods from '@messages/flexControls/resetPeriods';
import { ResetPeriod } from '@utils/resetPeriod';

interface CardControlBodyDetailsProps {
    cardControlItem?: GetCardControlsResponse;
    isLoading?: boolean;
    noOfColumnsPerRow?: number;
}

const AccountControlBodyDetails = ({
    cardControlItem = {} as GetCardControlsResponse,
    isLoading,
    noOfColumnsPerRow = 4,
}: CardControlBodyDetailsProps) => {
    const { formatMessage } = useIntl();

    const resetPeriod =
        cardControlItem?.limit_duration !== undefined
            ? getEnumTranslation(ResetPeriod(cardControlItem?.limit_duration), {
                  messages: resetPeriods,
                  formatMessage: formatMessage,
              }).toString()
            : '';

    const textBlocks = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: 'CardControl.processCodes',
                    defaultMessage: 'Processing codes',
                }),
                value: cardControlItem?.processing_codes?.join(', '),
            },
            {
                label: formatMessage({
                    id: 'CardControl.currencyCode',
                    defaultMessage: 'Currency code',
                }),
                value: cardControlItem?.currency_code,
            },
            {
                label: formatMessage({
                    id: 'CardControl.beneficiaryId',
                    defaultMessage: 'Beneficiary ID',
                }),
                value: cardControlItem?.beneficiary_id,
            },
            {
                label: formatMessage({
                    id: 'CardControl.overrideControls',
                    defaultMessage: 'Override controls',
                }),
                value: cardControlItem?.override_controls?.join(', '),
            },
            {
                label: formatMessage({
                    id: 'CardControl.timeZone',
                    defaultMessage: 'Time zone',
                }),
                value: cardControlItem?.time_zone,
            },
            {
                label: formatMessage({
                    id: 'CardControl.maxLimit',
                    defaultMessage: 'Max limit',
                }),
                value: cardControlItem?.max_limit,
            },
            {
                label: formatMessage({
                    id: 'CardControl.denyCode',
                    defaultMessage: 'Deny code',
                }),
                value: cardControlItem?.deny_code,
            },
            {
                label: formatMessage({
                    id: 'CardControl.active',
                    defaultMessage: 'Active',
                }),
                value: cardControlItem?.active
                    ? formatMessage({
                          id: 'Yes',
                          defaultMessage: 'Yes',
                      })
                    : formatMessage({
                          id: 'No',
                          defaultMessage: 'No',
                      }),
            },
            {
                label: formatMessage({
                    id: 'CardControl.limitDuration',
                    defaultMessage: 'Limit duration',
                }),
                value: cardControlItem?.limit_duration
                    ? resetPeriod
                    : cardControlItem?.limit_duration,
            },
        ];
    }, [cardControlItem, formatMessage, resetPeriod]);

    return (
        <TextDisplayBlock
            isLoading={isLoading}
            textBlocks={textBlocks}
            noOfColumnsPerRow={noOfColumnsPerRow}
        />
    );
};

export default AccountControlBodyDetails;
